import { useState, useMemo } from "react";

import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Switch } from "../../components/fields/base/Switch";
import { MdRotateLeft } from "react-icons/md";
import { formatCurrency, formatNumber, formatPercent } from "../../utils/formatNumber";
import { Button } from "../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../components/core/blocks/MetricSelector";
import { createAppColumnHelper, useClientTable } from "../../components/table/tableFeatures";
import { usePersistedTableState } from "../../components/table/usePersistedTableState";
import { customFilterFn } from "../../components/table/filterFns";
import { resetTable } from "../../components/table/utils";
import { RT8Pagination } from "../../components/table/Pagination";
import { Table } from "../../components/table/Table";
import { RT8FilterWidget } from "../merchandising/components/tables/FilterWidget";
import { css } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import { useSimpleCan } from "../../config/casl/casl";

const columnHelper = createAppColumnHelper();

const PriceDetails = ({ optData, marketPlace }) => {
  const { t } = useTranslation(["pricingv2", "common"]);
  const [daysWithPrice, setDaysWithPrice] = useState(optData?.pricing_org?.days_with_price);
  const userCanExportPricingPlans = useSimpleCan("export", "PricingPlan");

  const tableData = useMemo(() => {
    return (
      optData?.pricing_data
        ?.filter((r) => r.num_days_at_price !== 0 && r.num_days_at_price >= daysWithPrice)
        ?.map((d) => ({
          ...d,
          cr: d?.qty_sold > 0 && d?.page_views > 0 ? d.qty_sold / d.page_views : 0,
        })) ?? []
    );
  }, [optData, daysWithPrice]);

  const columns = useMemo(() => {
    const mp = marketPlace.marketPlace;
    return [
      columnHelper.accessor("price", {
        header: t(($) => $.pricingv2["Price"]),
        size: 100,
        enableHiding: false,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("num_days_at_price", {
        header: t(($) => $.pricingv2["Days at Price"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue(), { decimalPlaces: 1 }),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("qty_sold", {
        header: t(($) => $.pricingv2["Quantity Sold"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue(), { decimalPlaces: 1 }),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("avg_daily_sales", {
        header: t(($) => $.pricingv2["Daily Units"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue()),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("avg_monthly_sales", {
        header: t(($) => $.pricingv2["Monthly Units"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue()),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("cogs", {
        header: t(($) => $.pricingv2["COGS"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("org_profit", {
        header: t(($) => $.pricingv2["Margin"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("monthly_profit", {
        header: t(($) => $.pricingv2["Monthly Profit"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("page_views", {
        header: t(($) => $.pricingv2["Page Views"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatNumber(getValue(), { decimalPlaces: 1 }),
        meta: { justify: "end", columnFilterOptions: { format: "number" } },
      }),
      columnHelper.accessor("cr", {
        header: t(($) => $.pricingv2["CR"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatPercent(getValue(), { decimalPlaces: 1 }),
        meta: { justify: "end", columnFilterOptions: { format: "percent" } },
      }),
      columnHelper.accessor("vat", {
        header: t(($) => $.pricingv2["VAT"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("landed_cogs", {
        header: t(($) => $.pricingv2["Landed Cost"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("ship_cost", {
        header: t(($) => $.pricingv2["Shipping Cost"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("closing_fees", {
        header: t(($) => $.pricingv2["Closing Fee"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
      columnHelper.accessor("referral_fees", {
        header: t(($) => $.pricingv2["Referral Fee"]),
        size: 140,
        minSize: 80,
        cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace: mp, compact: true }),
        meta: { justify: "end", columnFilterOptions: { format: "currency" } },
      }),
    ];
  }, [t, marketPlace.marketPlace]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "priceHistoryDetailsTable",
    initialState: {
      sorting: [{ id: "price", desc: false }],
      columnVisibility: { vat: false, landed_cogs: false, ship_cost: false, closing_fees: false, referral_fees: false },
    },
  });

  const table = useClientTable({
    data: tableData,
    columns,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    initialState,
    state,
    ...setters,
  });

  const downloadCSV = () => {
    const data = [
      [
        "price",
        "day_at_price",
        "quantity_sold",
        "daily_units",
        "monthly_units",
        "cogs",
        "margin",
        "monthly_profit",
        "page_views",
        "cr",
      ],
    ];

    tableData.forEach((row) => {
      data.push([
        row.price ? row.price.toFixed(2) : 0,
        row.num_days_at_price ? row.num_days_at_price.toFixed(2) : 0,
        row.qty_sold ? row.qty_sold.toFixed(2) : 0,
        row.avg_daily_sales ? row.avg_daily_sales.toFixed(2) : 0,
        row.avg_monthly_sales ? row.avg_monthly_sales.toFixed(2) : 0,
        row.cogs ? row.cogs.toFixed(2) : 0,
        row.org_profit ? row.org_profit.toFixed(2) : 0,
        row.monthly_profit ? row.monthly_profit.toFixed(2) : 0,
        row.page_views ? row.page_views.toFixed(2) : 0,
        row.cr ? row.cr.toFixed(4) : 0,
      ]);
    });

    const csvContent = data.map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "price_history_download.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <HStack className={css({ display: "flex", justifyContent: "end", paddingRight: "medium" })}>
        <Switch
          isSelected={daysWithPrice !== 0}
          onChange={() => {
            if (daysWithPrice === 0) {
              setDaysWithPrice(optData.pricing_org.days_with_price - 1);
            } else {
              setDaysWithPrice(0);
            }
          }}
        />
        {daysWithPrice !== 0 ?
          <span className="ps-3">
            {t(($) => $.pricingv2["Show prices with less than {days} days of data"], {
              days: optData.pricing_org.days_with_price,
            })}
          </span>
        : <span className="ps-3">
            {t(($) => $.pricingv2["Hide prices with less than {days} days of data"], {
              days: optData.pricing_org.days_with_price,
            })}
          </span>
        }
      </HStack>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
      {userCanExportPricingPlans && (
        <Button
          onPress={() => {
            downloadCSV();
          }}
          className={css({ marginLeft: "5px" })}
        >
          {t(($) => $.pricingv2["Download CSV"])}
        </Button>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  marketPlace: state.marketPlace,
});

export default connect(mapStateToProps)(PriceDetails);
